// contexts/ChatContext.js
// Used with AddToChat UI component 
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveMessage } from '../../utils/db-messaging';

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const [pendingContent, setPendingContent] = useState(null);
  const navigate = useNavigate();

  // Method for queuing content to MessageInput
  const addToMessageInput = useCallback((content) => {
    setPendingContent(content);
    navigate('/home'); // Navigate to home
  }, [navigate]);

  // Method for auto-sending content to chat
  const sendToChat = useCallback(async (userId, content, metadata = {}) => {
    try {
      // Create a new chat with the content
      const formattedMessage = {
        sender: "user",
        text: content,
        metadata: {
          source: metadata.source || 'community',
          type: metadata.type || 'article',
          reference: metadata.id,
          ...metadata
        }
      };

      const { conversationId } = await saveMessage(userId, formattedMessage);

      // Navigate to the chat with the new conversation
      navigate('/home', { 
        state: { 
          newChat: true, 
          conversationId,
          returnToChat: true
        }
      });

      return { success: true, conversationId };
    } catch (error) {
      console.error('Error sending to chat:', error);
      return { success: false, error };
    }
  }, [navigate]);

  const clearPendingContent = useCallback(() => {
    setPendingContent(null);
  }, []);

  const value = {
    pendingContent,
    addToMessageInput,
    sendToChat,
    clearPendingContent
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatActions = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatActions must be used within a ChatProvider');
  }
  return context;
};

// Hook for handling chat content
export const useAddToChat = () => {
  const { addToMessageInput, sendToChat } = useChatActions();

  const handleAddToChat = useCallback((content, options = {}) => {
    const { autoSend, userId, metadata } = options;

    if (autoSend && userId) {
      return sendToChat(userId, content, metadata);
    } else {
      addToMessageInput(content);
      return Promise.resolve({ success: true });
    }
  }, [addToMessageInput, sendToChat]);

  return handleAddToChat;
};